"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { createClient } from "@/lib/supabase/client";
import type { Profile, Role, Entreprise } from "@/types";

export interface EntrepriseContext {
  id: string;
  km_rate: number | null;
  mode_frais_km: string;
  facturation_stripe_active: boolean;
  stripe_subscription_status: string;
  logo_url: string | null;
  couleur_principale: string;
  couleur_secondaire: string;
  nom: string;
  onboarding_complete: boolean;
  onboarding_step: number;
  pause_minutes: number;
  seuil_pause_heures: number | null;
  pause_exclut_heures_nuit: boolean;
  pas_de_pause_nuit: boolean;
  panier_actif: boolean;
  panier_duree_min_heures: number;
  panier_plage_debut: string;
  panier_plage_fin: string;
  panier_nuit: boolean;
  jour_off_actif: boolean;
  jour_off_iso: number;
  heures_hebdo_min: number;
  cumul_perso_service: boolean;
  seuil_ecart_equipe_minutes: number | null;
  heures_jour_conge: number;
  /** Phase 28 : format du fichier d'extraction ('standard' | 'variables_paie'). */
  format_extraction?: string;
  /** Phase 28 : panier de nuit si au moins N h entre 21 h et 6 h (null = ancienne règle). */
  panier_nuit_heures_plage?: number | null;
  mode_cumul_heures: string;
  heures_reference_jour: number;
  taux_vehicule_conducteur: number | null;
  taux_vehicule_passager: number | null;
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  role: Role | null;
  entreprise: EntrepriseContext | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshEntreprise: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  profile: null,
  role: null,
  entreprise: null,
  loading: true,
  signOut: async () => {},
  refreshEntreprise: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [entreprise, setEntreprise] = useState<EntrepriseContext | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    const init = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        await loadProfile(session.user.id, session.user.email);
      }
      setLoading(false);
    };
    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        setUser(session.user);
        await loadProfile(session.user.id, session.user.email);
      } else {
        setUser(null);
        setProfile(null);
        setEntreprise(null);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async (userId: string, userEmail?: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();
    if (data) {
      const p = data as Profile;
      // Synchroniser l'email si auth.users a changé (ex: reset, confirmation changement)
      if (userEmail && p.email !== userEmail) {
        const { data: updated } = await supabase
          .from("profiles")
          .update({ email: userEmail })
          .eq("id", userId)
          .select("*")
          .single();
        if (updated) {
          setProfile(updated as Profile);
          await loadEntreprise(updated.entreprise_id);
          return;
        }
      }
      setProfile(p);
      await loadEntreprise(p.entreprise_id);
    }
  };

  const loadEntreprise = async (entrepriseId?: string) => {
    const eid = entrepriseId || profile?.entreprise_id;
    if (!eid) return;
    const { data: e } = await supabase
      .from("entreprises")
      .select("id, nom, logo_url, couleur_principale, couleur_secondaire, km_rate, mode_frais_km, facturation_stripe_active, stripe_subscription_status, onboarding_complete, onboarding_step, pause_minutes, seuil_pause_heures, pause_exclut_heures_nuit, pas_de_pause_nuit, mode_cumul_heures, heures_reference_jour, taux_vehicule_conducteur, taux_vehicule_passager, panier_actif, panier_duree_min_heures, panier_plage_debut, panier_plage_fin, panier_nuit, jour_off_actif, jour_off_iso, heures_hebdo_min, cumul_perso_service, seuil_ecart_equipe_minutes, heures_jour_conge, format_extraction, panier_nuit_heures_plage")
      .eq("id", eid)
      .single();
    if (e) {
      setEntreprise(e as EntrepriseContext);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setEntreprise(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider
      value={{ user, profile, role: profile?.role ?? null, entreprise, loading, signOut, refreshEntreprise: () => loadEntreprise() }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
